import React, { useEffect, useState, useCallback } from "react";
import { CalendarDays, Monitor, Cpu, Smartphone } from "lucide-react";

const PRIORITY = (p) => {
  if (p.device_logged_in) return 0;
  if (p.web_logged_in) return 1;
  if (p.has_plan_today) return 2;
  return 3;
};

export const isActivePerson = (person) => !!(
  person?.web_logged_in
  || person?.device_logged_in
  || person?.active_device
);

const LiveViewPeoplePanel = ({ embedded = false, scope = "active" }) => {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPeople = useCallback(() => {
    Promise.all([
      fetch("/api/live-view/people", { credentials: "include" }).then((r) => r.json()),
      fetch("/api/device-sessions", { credentials: "include" }).then((r) => r.json()),
      fetch("/api/v1/devices", { credentials: "include" }).then((r) => r.json()),
    ])
      .then(([peopleData, sessionData, deviceData]) => {
        const deviceNames = new Map(
          (deviceData.devices || []).map((device) => [device.device_id, device.device_name || device.device_id])
        );
        const activeByUser = new Map();
        (sessionData.sessions || []).forEach((session) => {
          if (session.logout_at || activeByUser.has(String(session.user_id))) return;
          activeByUser.set(String(session.user_id), {
            ...session,
            device_name: deviceNames.get(session.device_id) || session.device_id,
          });
        });
        const enriched = (peopleData.people || []).map((person) => ({
          ...person,
          active_device: activeByUser.get(String(person.user_id)) || null,
        }));
        const sorted = enriched.slice().sort((a, b) => PRIORITY(a) - PRIORITY(b));
        setPeople(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchPeople();
    const id = setInterval(fetchPeople, 5000);
    return () => clearInterval(id);
  }, [fetchPeople]);

  const visiblePeople = scope === "active" ? people.filter(isActivePerson) : people;

  return (
    <div className="flex max-h-64 flex-col">
      {!embedded && <div className="px-3 pt-3 pb-2 border-b border-slate-700 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-300 uppercase tracking-wide">People</span>
        <button
          type="button"
          onClick={fetchPeople}
          className="text-slate-500 hover:text-slate-300 text-xs"
          title="Refresh"
        >
          ↻
        </button>
      </div>}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-slate-500 text-xs">Loading...</div>
      ) : (
        <div className="min-h-0 overflow-y-auto">
          {visiblePeople.map((p) => {
            const WebIcon = p.type === "cleaner" ? Smartphone : Monitor;
            return (
              <div
                key={p.user_id}
                className="flex items-center gap-2 border-b border-slate-800 px-3 py-2"
              >
                {p.photo_url ? (
                  <img
                    src={p.photo_url}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover shrink-0 bg-slate-700"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
                    <span className="text-xs text-slate-300 font-medium">
                      {(p.name || p.email || "?")[0].toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="flex-1 text-xs text-slate-200 truncate">{p.name || p.email}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <CalendarDays
                    size={13}
                    className={p.has_plan_today ? "text-slate-400" : "text-slate-700"}
                    title="Scheduled today"
                  />
                  <WebIcon
                    size={13}
                    className={p.web_logged_in ? "text-blue-400" : "text-slate-700"}
                    title={p.type === "cleaner" ? "Phone sign-in" : "Web sign-in"}
                  />
                  {p.active_device && (
                    <span className="flex items-center gap-1 max-w-28 text-teal-400" title={`Signed in: ${p.active_device.device_name}`}>
                      <Cpu size={13} className="shrink-0" />
                      <span className="truncate text-[9px]">{p.active_device.device_name}</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {visiblePeople.length === 0 && (
            <div className="text-slate-500 text-xs text-center py-8">No data</div>
          )}
        </div>
      )}

      <div className="px-3 py-2 border-t border-slate-700 flex gap-3 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><CalendarDays size={11} className="text-slate-400" /> Scheduled</span>
        <span className="flex items-center gap-1"><Smartphone size={11} className="text-blue-400" /> Phone</span>
        <span className="flex items-center gap-1"><Monitor size={11} className="text-blue-400" /> Admin</span>
        <span className="flex items-center gap-1"><Cpu size={11} className="text-teal-400" /> Device</span>
      </div>
    </div>
  );
};

export default LiveViewPeoplePanel;
